import type { API, BlockAPI } from '@editorjs/editorjs';

interface AlignmentTuneConfig {
  default?: 'left' | 'center' | 'right';
}

interface AlignmentTuneData {
  alignment: 'left' | 'center' | 'right';
}

export default class AlignmentTune {
  private api: API;
  private block: BlockAPI;
  private data: AlignmentTuneData;
  private config: AlignmentTuneConfig;
  private wrapper: HTMLElement | null = null;

  private readonly CSS = {
    alignment: {
      left: 'ce-tune-alignment--left',
      center: 'ce-tune-alignment--center',
      right: 'ce-tune-alignment--right',
    },
  };

  private readonly alignmentSettings = [
    {
      name: 'left' as const,
      icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 64 64">
        <path d="m54 8h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/>
        <path d="m54 52h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/>
        <path d="m10 23h28c1.104 0 2-.896 2-2s-.896-2-2-2h-28c-1.104 0-2 .896-2 2s.896 2 2 2z"/>
        <path d="m54 30h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/>
        <path d="m10 45h28c1.104 0 2-.896 2-2s-.896-2-2-2h-28c-1.104 0-2 .896-2 2s.896 2 2 2z"/>
      </svg>`,
    },
    {
      name: 'center' as const,
      icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 64 64">
        <path d="m54 8h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/>
        <path d="m54 52h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/>
        <path d="m46 23c1.104 0 2-.896 2-2s-.896-2-2-2h-28c-1.104 0-2 .896-2 2s.896 2 2 2z"/>
        <path d="m54 30h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/>
        <path d="m46 45c1.104 0 2-.896 2-2s-.896-2-2-2h-28c-1.104 0-2 .896-2 2s.896 2 2 2z"/>
      </svg>`,
    },
    {
      name: 'right' as const,
      icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 64 64">
        <path d="m54 8h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/>
        <path d="m54 52h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/>
        <path d="m54 19h-28c-1.104 0-2 .896-2 2s.896 2 2 2h28c1.104 0 2-.896 2-2s-.896-2-2-2z"/>
        <path d="m54 30h-44c-1.104 0-2 .896-2 2s.896 2 2 2h44c1.104 0 2-.896 2-2s-.896-2-2-2z"/>
        <path d="m54 41h-28c-1.104 0-2 .896-2 2s.896 2 2 2h28c1.104 0 2-.896 2-2s-.896-2-2-2z"/>
      </svg>`,
    },
  ];

  static get isTune(): boolean {
    return true;
  }

  constructor({ api, data, config, block }: { api: API; data?: AlignmentTuneData; config?: AlignmentTuneConfig; block: BlockAPI }) {
    this.api = api;
    this.block = block;
    this.config = config || {};

    // Set default alignment: use provided data, fallback to config default, or 'left'
    const defaultAlignment = this.config.default || 'left';
    this.data = data || { alignment: defaultAlignment };
  }

  render(): HTMLElement {
    const container = document.createElement('div');

    this.alignmentSettings.forEach((setting) => {
      const button = document.createElement('button');
      button.classList.add(this.api.styles.settingsButton);
      button.innerHTML = setting.icon;
      button.type = 'button';

      // Mark active button
      if (setting.name === this.data.alignment) {
        button.classList.add(this.api.styles.settingsButtonActive);
      }

      button.addEventListener('click', () => {
        this.setAlignment(setting.name);
      });

      container.appendChild(button);
    });

    return container;
  }

  private setAlignment(alignment: 'left' | 'center' | 'right'): void {
    this.data.alignment = alignment;

    // Update wrapper classes
    if (this.wrapper) {
      // Remove all alignment classes
      Object.values(this.CSS.alignment).forEach((className) => {
        this.wrapper.classList.remove(className);
      });
      // Add new alignment class
      this.wrapper.classList.add(this.CSS.alignment[alignment]);
    }

    // Trigger onChange to save the data
    this.block.dispatchChange();
  }

  wrap(blockContent: HTMLElement): HTMLElement {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add(this.CSS.alignment[this.data.alignment]);
    this.wrapper.appendChild(blockContent);
    return this.wrapper;
  }

  save(): AlignmentTuneData {
    return this.data;
  }
}
